import { ICardiacState } from '../../procedures/cardiac/store/cardiac.types'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IItem } from '../../timeline/store/timeline.types'
import { IOptions } from '../../../common/common.types'
import { IEKGEvent } from '../store/ekgImport.types'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { getCardiacFilters } from '../../procedures/cardiac/utilities/saveCardiac.util'
import { handleNemsisCodeError, getUpdatedProcedureStateWithNemsisCode } from '../../../utilities/nemsisCode.util'

interface CreateCardiacProcedureProps {
  procedureEvent: IEKGEvent
  cardiacOptions: IOptions
  pacingModeOptions: IOptions
  defibTypeOptions: IOptions
  waveformTypeOptions: IOptions
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface CardiacProcedureObject {
  timelineItem: IItem
  cardiacState: ICardiacState
}

const createCardiacProcedure = (props: CreateCardiacProcedureProps): CardiacProcedureObject => {
  const {
    procedureEvent,
    cardiacOptions,
    pacingModeOptions,
    defibTypeOptions,
    waveformTypeOptions,
    ptaCutoff,
    nemsisCodeOptions,
  } = props

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const cardiacOption = cardiacOptions.find((option) => (option.id ?? '').toString() === procedureEvent.procedureAction)
  const itemComments = procedureEvent.procedureComments || ''

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  const isExternalPacing = cardiacOption?.name === 'External Pacing'
  const isDefib = cardiacOption?.name === 'Defibrillation'

  const heartRate = isExternalPacing ? procedureEvent.procedureDetail1 : ''
  const mA = isExternalPacing ? procedureEvent.procedureDetail2 : ''
  const joules = isDefib ? procedureEvent.procedureDetail2 : ''

  const pacingModeOption = pacingModeOptions.find((option) => {
    return isExternalPacing && (option.id ?? '').toString() === procedureEvent.procedureDetail3
  })
  const defibTypeOption = defibTypeOptions.find((option) => {
    return isDefib && (option.id ?? '').toString() === procedureEvent.procedureDetail1
  })
  const waveformTypeOption = waveformTypeOptions.find((option) => {
    return isDefib && (option.id ?? '').toString() === procedureEvent.procedureDetail3
  })

  const defibSection = `${joules ? 'Joules: ' + joules + ', ' : ''}${
    defibTypeOption ? 'Defib Type: ' + (defibTypeOption?.name || '') + ', ' : ''
  }${waveformTypeOption ? 'Type: ' + (waveformTypeOption?.name || '') + ', ' : ''}`

  const itemSubHeader = `By , Action: ${
    cardiacOption?.name || ''
  }, ${defibSection}Success: Yes, Comments: ${itemComments}.`

  let cardiacState: ICardiacState = {
    key: clientEventId,
    joulesHiddenFlag: true,
    leadChangeHiddenFlag: true,
    defibTypeHiddenFlag: true,
    typeHiddenFlag: true,
    action: cardiacOption ?? null,
    joules: joules || '',
    leadChange: [],
    defibType: defibTypeOption || null,
    waveformType: waveformTypeOption || null,
    procedureComplications: [],
    response: null,
    responsePNNV: null,
    authorization: null,
    comments: itemComments,
    addToTimeline: true,
    updateInputRows: false,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    assessedBy: null,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    type: 'Cardiac',
    saveResponse: '',
    rolePerforming: null,
    rolePerformingPNNV: null,
    mAHiddenFlag: true,
    heartRateHiddenFlag: true,
    pacingModeHiddenFlag: true,
    compressionRateHiddenFlag: true,
    locationHiddenFlag: true,
    mA: mA || '',
    heartRate: heartRate || '',
    pacingMode: pacingModeOption || null,
    compressionRate: null,
    location: null,
    mechanicalCprTypeHiddenFlag: true,
    mechanicalCprType: null,
    isEkgImport: 1,
    cardiacDefaults: null,
    nemsisCode: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
  }
  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.CARDIAC_PROCEDURE,
    cardiacOption?.name || null,
    {
      filter1: getCardiacFilters(cardiacState).filter1Field,
      filter2: getCardiacFilters(cardiacState).filter2Field,
      filter3: getCardiacFilters(cardiacState).filter3Field,
    },
    clientEventId
  )
  const timelineItem: IItem = {
    key: clientEventId,
    itemType: 'Procedures',
    event: 'Cardiac',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_CARDIAC_PROCEDURE_ITEM,
    defaultHref: TabConstants.CARDIAC_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientCorrelationId,
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  try {
    cardiacState = getUpdatedProcedureStateWithNemsisCode({
      procedureState: cardiacState,
      actionField: (s) => s.action?.name,
      nemsisCodeOptions,
      ...getCardiacFilters(cardiacState),
    })
  } catch (error: unknown) {
    cardiacState = handleNemsisCodeError(error, cardiacState)
  }

  const cardiacProcedureObject: CardiacProcedureObject = {
    cardiacState,
    timelineItem,
  }

  return cardiacProcedureObject
}

export default createCardiacProcedure
